import React, { Suspense, lazy, useMemo } from "react";
import { Box, CircularProgress, Stack } from "@mui/material";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";

import Header from "../00.Header";
import AppContainer from "./common/Container";
import { buildShellModules, resolveActiveModuleKey } from "./const";

const DashboardPage = lazy(() => import(/* webpackChunkName: "admin-shell-dashboard" */ "../Dashboard"));
const ProfilePage = lazy(() => import(/* webpackChunkName: "admin-shell-profile" */ "../Profile"));
const UsersView = lazy(() => import(/* webpackChunkName: "admin-shell-users" */ "../Users"));
const RolesView = lazy(() => import(/* webpackChunkName: "admin-shell-roles" */ "../Roles"));
const PermissionsView = lazy(() => import(/* webpackChunkName: "admin-shell-permissions" */ "../Permissions"));
const ModulesView = lazy(() => import(/* webpackChunkName: "admin-shell-modules" */ "../Modules"));
const ModulesCatalogView = lazy(() =>
  import(/* webpackChunkName: "admin-shell-modules-catalog" */ "../Modules/CatalogView"),
);

function ShellLoading() {
  return (
    <Stack alignItems="center" justifyContent="center" sx={{ minHeight: 240 }}>
      <CircularProgress size={26} color="secondary" />
    </Stack>
  );
}

export default function App({ links, authenticated }) {
  if (!authenticated) return null;

  const location = useLocation();

  const currentPath = location.pathname;
  const currentURI = `${location.pathname}${location.search}`;
  const catalogTab = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("tab") === "groups" ? "groups" : "apps";
  }, [location.search]);

  const modules = useMemo(() => buildShellModules({ links }), [links]);
  const activeKey = useMemo(
    () => resolveActiveModuleKey(modules, currentPath, currentURI),
    [modules, currentPath, currentURI],
  );

  return (
    <AppContainer>
      <Header modules={modules} activeKey={activeKey} />
      <Box
        sx={{
          width: "100%",
          boxSizing: "border-box",
          px: { xs: 1.25, sm: 1.5, md: 2 },
          pt: { xs: 1, sm: 1.5 },
          pb: 2,
          overflowX: "clip",
        }}
      >
        <Suspense fallback={<ShellLoading />}>
          <Routes>
            <Route path="/-" element={<DashboardPage links={links} />} />
            <Route path="/-/" element={<DashboardPage links={links} />} />
            <Route path="/-/perfil" element={<ProfilePage />} />
            <Route path="/perfil" element={<ProfilePage />} />

            <Route path="/-/users/*" element={<UsersView />} />
            <Route path="/-/roles/*" element={<RolesView />} />
            <Route path="/-/permissions/*" element={<PermissionsView />} />
            <Route path="/-/modules/catalog" element={<ModulesCatalogView tab={catalogTab} />} />
            <Route path="/-/modules/*" element={<ModulesView />} />

            <Route path="*" element={<Navigate to="/-/" replace />} />
          </Routes>
        </Suspense>
      </Box>
    </AppContainer>
  );
}
